import React, { useState, useEffect } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import DealDetailModal from './DealDetailModal'
import { splitDealItems } from './splitDealItems'
import { ConsignmentBadge } from '../shared/ConsignmentBadge.jsx'
import { consignmentBlockReason, parseSplitPct } from '../../utils/consignmentStates'
import { pendingReasonChip } from '../../utils/paymentPending'
import OffOnStaging from '../OffOnStaging'
import DealLink from '../shared/DealLink.jsx'
import TriangleAlertIcon from '../shared/TriangleAlertIcon.jsx'
import { changedNote, PAYEE_CHECK, CLIPBOARD_PREFIX } from '../../utils/payoutChanged'
import { figuresFromPreflight, figuresFromDeal, isStoreCreditDeal } from '../../utils/storeCredit'
import StoreCreditFigures from './StoreCreditFigures.jsx'

// Clipboard-only. On SCREEN a masked value is shown as-is — ****5678 says which
// account is on file, where a placeholder reads as "the customer never gave us
// details". In the CLIPBOARD it cannot: `Acc: ****5678, Sort: null` pastes into
// a banking app looking payable. So an unrevealed value is named here instead.
const MISSING = 'not available'

// The payer flag (slice 9b): a recent change to the payout details, in words
// and an icon, never colour alone (A7). Shown beside the account and again in
// the confirm modal, so the payer checks the payee name before paying.
function ChangedFlag({ note }) {
  return (
    <div data-payout-changed style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 4, padding: '4px 8px', borderRadius: 4, background: '#fef3c7', color: '#92400e', fontSize: 12 }}>
      <TriangleAlertIcon size={14} style={{ marginTop: 2 }} />
      <div>
        <div style={{ fontWeight: 600 }}>{note}</div>
        <div>{PAYEE_CHECK}</div>
      </div>
    </div>
  )
}

function Payments() {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDeals, setSelectedDeals] = useState(new Set())
  const [processingPayment, setProcessingPayment] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [copyToast, setCopyToast] = useState(null)
  // Unmasked instruments, by deal id, for rows the operator has revealed this
  // session. Never persisted and never fetched with the grid — each entry cost
  // an audited decrypt on Prism's side.
  const [revealed, setRevealed] = useState({})
  const [revealing, setRevealing] = useState(new Set())
  // D8: the `instrument_changed_at` each reveal showed, by deal id, sent back
  // untouched with Mark Paid so Prism can refuse a payment to details that
  // changed after the reveal. A deal is absent when its reveal carried no
  // stamp (an older Prism): then nothing is sent, as before.
  const [seenStamps, setSeenStamps] = useState({})
  // Store credit figures from a preflight, by deal id (Prism strikes both).
  const [creditFacts, setCreditFacts] = useState({})
  // The held half of the page (spec 20260916): payable deals the server
  // holds back, each with its reason. Never selectable, never paid from here.
  const [pendingDeals, setPendingDeals] = useState([])
  const [pendingError, setPendingError] = useState(null)

  function showCopyToast(text) {
    setCopyToast(text)
    setTimeout(() => setCopyToast(null), 2000)
  }

  useEffect(() => {
    loadDeals()
  }, [])

  async function loadDeals() {
    setLoading(true)
    // Two lists, one page. Loaded independently so an outage on the held list
    // never blanks the payable one, and vice versa.
    const [awaiting, pending] = await Promise.allSettled([
      api.get('/api/deals/awaiting-payment'),
      api.get('/api/deals/pending-payment'),
    ])
    if (awaiting.status === 'fulfilled') {
      setDeals(awaiting.value.deals || [])
    } else {
      console.error('Failed to load deals:', awaiting.reason)
    }
    if (pending.status === 'fulfilled') {
      setPendingDeals(pending.value.deals || [])
      setPendingError(null)
    } else {
      console.error('Failed to load pending payments:', pending.reason)
      setPendingDeals([])
      setPendingError(pending.reason?.message || 'unknown error')
    }
    setLoading(false)
  }

  // Blocked rows: consignments without a recorded payout, and consignments
  // whose server-reported eligibility state is not payable (spec_20260817 —
  // the server is the authority; consignmentBlockReason only renders it).
  const payableDeals = deals.filter(d => !d.payoutMissing && !consignmentBlockReason(d))

  function toggleSelectAll() {
    if (selectedDeals.size === payableDeals.length) {
      setSelectedDeals(new Set())
    } else {
      setSelectedDeals(new Set(payableDeals.map(d => d.id)))
    }
  }

  function toggleSelect(dealId) {
    const deal = deals.find(d => d.id === dealId)
    if (deal?.payoutMissing || consignmentBlockReason(deal)) return
    const newSelected = new Set(selectedDeals)
    if (newSelected.has(dealId)) {
      newSelected.delete(dealId)
    } else {
      newSelected.add(dealId)
    }
    setSelectedDeals(newSelected)
  }

  // The explicit batch (spec 20260805): the ids + amount THIS page displayed.
  // Sending bare deal ids would let the server settle "whatever is payable at
  // flush time" — an item tested between page load and the pay click would be
  // silently married to a payment that never included its money.
  function paymentsPayload(dealIds) {
    return {
      payments: dealIds.map(id => {
        const d = deals.find(x => x.id === id)
        return {
          dealId: id,
          quoteItemIds: d?.payableItemIds || [],
          amountCents: Math.round((d?.effectiveAmount || 0) * 100),
          ...(Object.hasOwn(seenStamps, id) ? { instrumentChangedAtSeen: seenStamps[id] } : {}),
        }
      }),
    }
  }

  // Process PayPal payout via API
  async function processPayPalPayout(dealIds) {
    setProcessingPayment(true)
    setConfirmModal(null)
    try {
      const result = await api.post('/api/deals/paypal-payout', paymentsPayload(dealIds))
      if (result.success) {
        const summary = `PayPal payment processed!\n\nTotal Paid: £${result.totalPaid}\nDeals Processed: ${result.dealsProcessed}\nEmails Sent: ${result.emailsSent || 0}`
        // A deal that was paid but not moved to Closed Traded stays in this
        // queue and can be paid a second time, so it has to be named here — a
        // green summary over a partial failure is how that happens.
        if (result.errors?.length > 0) {
          alert(`${summary}\n\n⚠ ${result.errors.length} need attention:\n\n${result.errors.map(e => e.error).join('\n\n')}`)
        } else {
          alert(summary)
        }
        setSelectedDeals(new Set())
        loadDeals()
      }
    } catch (error) {
      alert(`PayPal Error: ${error.message}`)
    } finally {
      setProcessingPayment(false)
    }
  }

  // Mark as paid (bank transfer - manual)
  async function markAsPaid(dealIds) {
    setProcessingPayment(true)
    setConfirmModal(null)
    try {
      const result = await api.post('/api/deals/mark-paid', paymentsPayload(dealIds))
      if (result.success) {
        const message = `Marked ${result.processed} payment(s) as paid. ${result.emailsSent} email(s) sent.`
        if (result.errors?.length > 0) {
          alert(`${message}\n\nErrors: ${result.errors.map(e => e.error).join(', ')}`)
        } else {
          alert(message)
        }
        setSelectedDeals(new Set())
        loadDeals()
      }
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setProcessingPayment(false)
    }
  }

  // Issue store credit (Prism credits the seller's Shopify account, once per
  // payment, and completes the payment). An unconfirmed earlier attempt needs
  // the payer to check the customer's Shopify store credit history first.
  async function issueStoreCredit(dealIds, confirmed = new Set()) {
    setProcessingPayment(true)
    setConfirmModal(null)
    try {
      const body = paymentsPayload(dealIds)
      body.payments = body.payments.map(p => (confirmed.has(p.dealId) ? { ...p, confirmNotIssued: true } : p))
      const result = await api.post('/api/deals/issue-store-credit', body)
      const message = `Issued store credit for ${result.processed} deal(s). ${result.emailsSent} email(s) sent.`
      const review = (result.errors || []).filter(e => e.needsReview)
      if (result.errors?.length > 0) {
        alert(`${message}\n\nNeed attention:\n${result.errors.map(e => e.error).join('\n')}`)
      } else {
        alert(message)
      }
      if (review.length > 0 && window.confirm(
        'Open the customer\'s store credit history in Shopify and check nothing was credited for these deals. Has nothing been credited, and may Prism try again?'
      )) {
        await issueStoreCredit(review.map(e => e.dealId), new Set(review.map(e => e.dealId)))
        return
      }
      setSelectedDeals(new Set())
      loadDeals()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setProcessingPayment(false)
    }
  }

  // Reveal ONE row's instrument (H1). Used for an ad-hoc check without
  // committing to a payout run.
  async function revealOne(dealId) {
    setRevealing(prev => new Set(prev).add(dealId))
    try {
      const res = await api.get(`/api/deals/${dealId}/payment-preflight`)
      setRevealed(prev => ({ ...prev, [dealId]: res.payout_details || {} }))
      if (res.store_credit) setCreditFacts(prev => ({ ...prev, [dealId]: res.store_credit }))
      setSeenStamps(prev => withStamps(prev, [[dealId, res]]))
    } catch (error) {
      // Loud on purpose: a silent failure here leaves the masked value on
      // screen, which reads as "these ARE the details" — see MISSING.
      alert(`Could not read the payout details: ${error.message}`)
    } finally {
      setRevealing(prev => {
        const next = new Set(prev)
        next.delete(dealId)
        return next
      })
    }
  }

  // The FIRST stamp a deal's reveal showed in this page session is the one
  // Mark Paid sends, and a later reveal never moves it forward. The operator may
  // have paid from the first reveal, so a change landing between the two must
  // still be refused (D8); a reload is how a change is accepted.
  function withStamps(prev, revealsByDeal) {
    const next = { ...prev }
    for (const [dealId, reveal] of revealsByDeal) {
      if (Object.hasOwn(next, dealId)) continue
      if (Object.hasOwn(reveal, 'instrument_changed_at')) next[dealId] = reveal.instrument_changed_at
    }
    return next
  }

  // The grid row, with the change flag taken from a fresh reveal when it
  // carries one: the grid was loaded earlier and may not know of a newer change.
  function withFreshFlag(deal, reveal) {
    if (!reveal?.instrument_changed_at) return deal
    return {
      ...deal,
      payoutChangedAt: reveal.instrument_changed_at,
      payoutChangedBy: reveal.instrument_changed_source ?? deal.payoutChangedBy,
    }
  }

  // The instrument as the operator should see it: revealed where we have it,
  // masked otherwise. `paymentDetails` is only ever the masked projection.
  function instrumentFor(deal) {
    const real = revealed[deal.id]
    if (deal.paymentMethod === 'paypal') {
      return { paypal: real?.paypal_email || deal.paymentDetails || null, isReal: !!real }
    }
    const masked = deal.paymentDetails || {}
    return {
      name: real?.bank_name || masked.name || null,
      account: real?.bank_account || masked.account || null,
      sortCode: real?.bank_sort || masked.sortCode || null,
      isReal: !!real,
    }
  }

  function formatPaymentDetailsForClipboard(selectedDealData, unmasked) {
    const lines = selectedDealData
      .map(deal => {
        const real = unmasked[deal.id]
        // A flagged row carries its warning into the paste.
        const prefix = changedNote(deal) ? CLIPBOARD_PREFIX : ''
        if (deal.paymentMethod === 'paypal') {
          const paypal = real?.paypal_email || deal.paymentDetails || ''
          return paypal && `${prefix}${paypal}`
        }
        if (deal.paymentMethod === 'bank') {
          const masked = deal.paymentDetails || {}
          const name = real?.bank_name || masked.name || '-'
          // Interpolating the masked values raw pasted "Acc: ****4321,
          // Sort: null" into an operator's banking app as if it were payable.
          const account = real?.bank_account || MISSING
          const sortCode = real?.bank_sort || MISSING
          return `${prefix}${name}, Acc: ${account}, Sort: ${sortCode}`
        }
        return null
      })
      .filter(Boolean)
    return lines.join('\n')
  }

  async function openConfirmModal(dealIds) {
    let selectedDealData = deals.filter(d => dealIds.includes(d.id))
    const total = selectedDealData.reduce((sum, d) => sum + (d.effectiveAmount || 0), 0)

    // CR-3: reveal the whole run in ONE call, which is also ONE audit row.
    // Everything the grid holds is masked, so without this the clipboard is
    // unpayable by construction.
    let unmasked = {}
    let revealError = null
    try {
      const res = await api.post('/api/deals/payment-preflight', { dealIds })
      unmasked = Object.fromEntries(
        (res.results || []).map(row => [row.dealId, row.payout_details || {}])
      )
      setRevealed(prev => ({ ...prev, ...unmasked }))
      setCreditFacts(prev => ({
        ...prev,
        ...Object.fromEntries((res.results || []).filter(row => row.store_credit).map(row => [row.dealId, row.store_credit])),
      }))
      setSeenStamps(prev => withStamps(prev, (res.results || []).map(row => [row.dealId, row])))
      const freshById = new Map((res.results || []).map(row => [row.dealId, row]))
      selectedDealData = selectedDealData.map(d => withFreshFlag(d, freshById.get(d.id)))
    } catch (error) {
      revealError = error.message
    }

    let copiedToClipboard = false
    const clipboardText = formatPaymentDetailsForClipboard(selectedDealData, unmasked)
    // Never claim a copy when the reveal failed — the text would be masked
    // placeholders, and "copied to clipboard" is what makes an operator paste
    // it without looking.
    if (!revealError && clipboardText) {
      try {
        await navigator.clipboard.writeText(clipboardText)
        copiedToClipboard = true
      } catch {
        copiedToClipboard = false
      }
    }

    setConfirmModal({ dealIds, deals: selectedDealData, total, copiedToClipboard, revealError, unmasked })
  }

  // Both store credit figures for a row: this session's preflight when there is
  // one, else what the payment carries once it is initiated.
  function creditFiguresFor(deal) {
    return figuresFromPreflight(creditFacts[deal.id]) || figuresFromDeal(deal)
  }

  // Calculate stats (effectiveAmount = consignment payout where present, else deal amount)
  const totalAmount = payableDeals.reduce((sum, d) => sum + (d.effectiveAmount || 0), 0)
  const selectedTotal = deals
    .filter(d => selectedDeals.has(d.id))
    .reduce((sum, d) => sum + (d.effectiveAmount || 0), 0)

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <>
      {/* Header Stats */}
      <div className="stats-grid" style={{ marginTop: 'var(--space-4)' }}>
        <div className="stat-card">
          <div className="stat-value">{deals.length}</div>
          <div className="stat-label">Awaiting Payment</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--color-success)' }}>
            {formatCurrency(totalAmount)}
          </div>
          <div className="stat-label">Total Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={pendingDeals.length > 0 ? { color: 'var(--color-warning)' } : undefined}>
            {pendingDeals.length}
          </div>
          <div className="stat-label">Pending payment</div>
        </div>
        {selectedDeals.size > 0 && (
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--color-info)' }}>
              {formatCurrency(selectedTotal)}
            </div>
            <div className="stat-label">{selectedDeals.size} Selected</div>
          </div>
        )}
      </div>

      {/* Actions Bar */}
      {deals.length > 0 && (
        <div className="card" style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selectedDeals.size === payableDeals.length && payableDeals.length > 0}
              onChange={toggleSelectAll}
            />
            Select All
          </label>
          <OffOnStaging feature="payments">
          <button
            className="btn btn-primary"
            onClick={() => openConfirmModal([...selectedDeals])}
            disabled={selectedDeals.size === 0 || processingPayment}
          >
            {processingPayment ? 'Processing...' : `Pay Selected (${selectedDeals.size})`}
          </button>
          </OffOnStaging>
        </div>
      )}

      {/* Deals Table */}
      <div className="card" style={{ marginTop: 'var(--space-4)' }}>
        {deals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--muted)' }}>
            <div style={{ fontSize: 48, marginBottom: 'var(--space-3)' }}>💳</div>
            <h3>No Deals Awaiting Payment</h3>
            <p>All payments have been processed.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Deal</th>
                <th>Customer</th>
                <th style={{ textAlign: 'center', width: 60 }} title="* = the payment email will leave out the review link">Review</th>
                <th>Method</th>
                <th>Details</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ width: 80 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {deals.map(deal => {
                const blockReason = consignmentBlockReason(deal)
                return (
                <tr key={deal.id} onClick={() => setSelectedDeal(deal)} style={{ cursor: 'pointer' }} className="hover-row">
                  <td onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedDeals.has(deal.id)}
                      onChange={() => toggleSelect(deal.id)}
                      disabled={deal.payoutMissing || Boolean(blockReason)}
                    />
                  </td>
                  <td className="col-mobile-primary">
                    {/* A multi-item deal arrives as one ~400-char concatenated
                        string; rendering it verbatim made this the widest cell
                        in the app and pushed the Action column off screen at
                        every width — 1440px included. One line per item reads
                        as a list instead of a run-on. Single-item deals are
                        returned unchanged by splitDealItems, so they render
                        exactly as before. */}
                    <div className="deal-items">
                      {splitDealItems(deal.dealname).map((item, index) => (
                        <div key={index} style={{ fontWeight: 600 }}>{item}</div>
                      ))}
                      {!deal.dealname && <div style={{ fontWeight: 600 }}>Unnamed Deal</div>}
                    </div>
                    {deal.isConsignment && (
                      <div style={{ marginTop: 2 }}>
                        <ConsignmentBadge splitPct={parseSplitPct(deal.consignment_split_pct)} />
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      <DealLink deal={deal} onOpen={() => setSelectedDeal(deal)} /> | {formatDate(deal.createdate)}
                    </div>
                  </td>
                  <td>
                    <div>{deal.customer_name || '-'}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {deal.seller_email || 'No email'}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {deal.reviewLinkWithheld && (
                      <span title="No review link: this customer had a condition downgrade or issue trade on this payment's deals, or on any deal in the last 7 days" style={{ color: '#000', fontSize: 18, fontWeight: 700 }}>*</span>
                    )}
                  </td>
                  <td>
                    {deal.paymentMethod === 'paypal' && (
                      <span className="badge badge-info" style={{ fontSize: 'inherit' }}>PayPal</span>
                    )}
                    {deal.paymentMethod === 'bank' && (
                      <span className="badge badge-secondary" style={{ fontSize: 'inherit' }}>Bank Transfer</span>
                    )}
                    {isStoreCreditDeal(deal) && (
                      <span className="badge badge-success" style={{ fontSize: 'inherit' }}>Store credit</span>
                    )}
                  </td>
                  <td>
                    {isStoreCreditDeal(deal) && (
                      <StoreCreditFigures
                        figures={creditFiguresFor(deal)}
                        showing={revealing.has(deal.id)}
                        onShow={() => revealOne(deal.id)}
                      />
                    )}
                    {deal.paymentMethod && !isStoreCreditDeal(deal) && (() => {
                      const inst = instrumentFor(deal)
                      // Show the MASKED value rather than hiding it: ****5678
                      // tells the operator which account is on file and that
                      // one exists at all, which a placeholder does not. Only
                      // the copy control is withheld until it is the real
                      // thing — seeing a masked value is useful, pasting one
                      // into a banking app is the bug.
                      const rows = deal.paymentMethod === 'paypal'
                        ? [{ label: 'PayPal', value: inst.paypal || '-', copyable: inst.isReal }]
                        : [
                            // bank_name is stored in the clear, so it is always real.
                            { label: 'Name', value: inst.name || '-', copyable: true },
                            { label: 'Account', value: inst.account || '-', copyable: inst.isReal },
                            { label: 'Sort code', value: inst.sortCode || '-', copyable: inst.isReal },
                          ]
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {rows.map(({ label, value, copyable }) => (
                            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ color: 'var(--muted)' }}>{label}:</span>
                              <span>{value}</span>
                              {copyable && value && value !== '-' && (
                                <span
                                  title={`Copy ${label.toLowerCase()}`}
                                  style={{ cursor: 'pointer', userSelect: 'none', fontSize: 18 }}
                                  onClick={e => {
                                    e.stopPropagation()
                                    navigator.clipboard.writeText(value)
                                    showCopyToast(`${label}: ${value} has been copied to your clipboard`)
                                  }}
                                >📋</span>
                              )}
                            </div>
                          ))}
                          {changedNote(deal) && <ChangedFlag note={changedNote(deal)} />}
                          {!inst.isReal && (
                            <button
                              className="btn btn-sm btn-secondary"
                              style={{ marginTop: 4, alignSelf: 'flex-start' }}
                              disabled={revealing.has(deal.id)}
                              onClick={e => { e.stopPropagation(); revealOne(deal.id) }}
                              title="Decrypts the stored instrument. Audited on Prism."
                            >
                              {revealing.has(deal.id) ? 'Revealing…' : '🔓 Reveal'}
                            </button>
                          )}
                        </div>
                      )
                    })()}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {deal.payoutMissing ? (
                      <span style={{ background: '#fef2f2', color: '#b91c1c', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>
                        ⚠ No payout recorded
                      </span>
                    ) : (
                      formatCurrency(deal.effectiveAmount)
                    )}
                    {deal.payoutMissing && (
                      <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>
                        Consignment — no payout recorded, see Consignments
                      </div>
                    )}
                    {!deal.payoutMissing && blockReason && (
                      <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>
                        {blockReason}
                      </div>
                    )}
                    {deal.payableCount != null && deal.liveCount != null && deal.payableCount < deal.liveCount && (
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: 4, display: 'inline-block', whiteSpace: 'nowrap' }}>
                        Paying {deal.payableCount} of {deal.liveCount} items
                      </div>
                    )}
                    {deal.isConsignment && !deal.payoutMissing && (
                      <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>
                        {/* D16: split is the seller's share — payout = sale × split% */}
                        sale {formatCurrency(deal.final_sale_price)} × {deal.consignment_split_pct || 70}%
                        {parseFloat(deal.payout_deductions) > 0 && <> − {formatCurrency(deal.payout_deductions)} deductions</>}
                        {deal.payout_note && <> · {deal.payout_note}</>}
                      </div>
                    )}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <OffOnStaging feature="payments">
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => openConfirmModal([deal.id])}
                      disabled={processingPayment || deal.payoutMissing || Boolean(blockReason)}
                    >
                      Pay
                    </button>
                    </OffOnStaging>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pending payment (spec 20260916). The server decides membership and
          the reason; this only shows it. No checkbox, no Pay: the operator
          opens the deal to fix the reason (add payment details, receive the
          late box) and the row moves up on the next load. */}
      <div className="card" style={{ marginTop: 'var(--space-4)' }}>
        <div className="card-header">
          <h3 className="card-title">Pending payment</h3>
        </div>
        {pendingError ? (
          <div style={{ padding: 'var(--space-4)', color: 'var(--muted)' }}>
            Could not load the pending list. {pendingError}
          </div>
        ) : pendingDeals.length === 0 ? (
          <div style={{ padding: 'var(--space-4)', color: 'var(--muted)' }}>
            Nothing pending. Every deal on this page can be paid.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Deal</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Reason</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {pendingDeals.map(deal => {
                const chip = pendingReasonChip(deal)
                return (
                  <tr key={deal.id} onClick={() => setSelectedDeal(deal)} style={{ cursor: 'pointer' }} className="hover-row">
                    <td className="col-mobile-primary">
                      <div className="deal-items">
                        {splitDealItems(deal.dealname).map((item, index) => (
                          <div key={index} style={{ fontWeight: 600 }}>{item}</div>
                        ))}
                        {!deal.dealname && <div style={{ fontWeight: 600 }}>Unnamed Deal</div>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                        <DealLink deal={deal} onOpen={() => setSelectedDeal(deal)} /> | {formatDate(deal.createdate)}
                      </div>
                    </td>
                    <td>
                      <div>{deal.customer_name || '-'}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {deal.seller_email || 'No email'}
                      </div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {deal.payableCount != null && deal.liveCount != null
                        ? `${deal.payableCount} of ${deal.liveCount} payable`
                        : '-'}
                    </td>
                    <td>
                      {chip && (
                        <span className={`badge ${chip.badgeClass}`} style={{ fontSize: 'inherit', whiteSpace: 'nowrap' }}>
                          {chip.label}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>
                      {formatCurrency(deal.effectiveAmount)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Deal Detail Modal */}
      {selectedDeal && (
        <DealDetailModal
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onDealUpdated={loadDeals}
          canRequestReturn
        />
      )}

      {/* Confirm Payment Modal */}
      {confirmModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h2>Confirm Payment</h2>
              <button className="modal-close" onClick={() => setConfirmModal(null)}>&times;</button>
            </div>
            <div className="modal-body-scroll" style={{ maxHeight: 400 }}>
              <p style={{ marginBottom: 'var(--space-4)' }}>
                You are about to mark <strong>{confirmModal.deals.length} deal(s)</strong> as paid.
              </p>

              <div style={{ marginBottom: 'var(--space-4)' }}>
                {confirmModal.deals.map(deal => (
                  <div key={deal.id} style={{
                    padding: 'var(--space-3)',
                    background: 'var(--bg)',
                    borderRadius: 8,
                    marginBottom: 8
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{deal.dealname}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                          {deal.paymentMethod === 'paypal' ? `PayPal: ${deal.paymentDetails}` : isStoreCreditDeal(deal) ? 'Store credit' : 'Bank Transfer'}
                        </div>
                        <div style={{ fontSize: 12 }}>
                          {isStoreCreditDeal(deal) && (
                            <StoreCreditFigures figures={creditFiguresFor(deal)} />
                          )}
                        </div>
                        {deal.payableCount != null && deal.liveCount != null && deal.payableCount < deal.liveCount && (
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#b45309', marginTop: 2 }}>
                            Paying {deal.payableCount} of {deal.liveCount} items — the rest pays after testing
                          </div>
                        )}
                      </div>
                      <div style={{ fontWeight: 700 }}>{formatCurrency(deal.effectiveAmount)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {confirmModal.deals.some(d => changedNote(d)) && (
                <div data-changed-recently style={{ marginBottom: 'var(--space-4)' }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Changed recently</div>
                  {confirmModal.deals.filter(d => changedNote(d)).map(deal => (
                    <div key={deal.id} style={{ marginBottom: 6 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{deal.dealname}</div>
                      <ChangedFlag note={changedNote(deal)} />
                    </div>
                  ))}
                </div>
              )}

              <div style={{
                padding: 'var(--space-4)',
                background: 'linear-gradient(180deg, rgba(221,245,234,.75), rgba(255,255,255,.70))',
                borderRadius: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontWeight: 600 }}>Total Amount:</span>
                <span style={{ fontSize: 24, fontWeight: 800, color: 'var(--color-success)' }}>
                  {formatCurrency(confirmModal.total)}
                </span>
              </div>

              {confirmModal.copiedToClipboard && (
                <div style={{
                  marginTop: 'var(--space-3)',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'rgb(78 128 152 / 0.1)',
                  borderRadius: 8,
                  fontSize: 13,
                  color: 'var(--blue-monday)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <span>&#x2713;</span>
                  <span>Payment details copied to clipboard</span>
                </div>
              )}

              {confirmModal.revealError && (
                <div style={{
                  marginTop: 'var(--space-3)',
                  padding: 'var(--space-2) var(--space-3)',
                  background: '#fef2f2',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#b91c1c'
                }}>
                  <strong>Could not read the payout details.</strong> Nothing has been
                  copied — the grid only ever holds masked values, so paying from what
                  is on screen would fail. {confirmModal.revealError}
                </div>
              )}

              <p style={{ marginTop: 'var(--space-4)', fontSize: 13, color: 'var(--muted)' }}>
                This will move the deals to "Closed Traded" and send payment confirmation emails to customers.
              </p>
            </div>
            <div className="modal-footer" style={{ padding: 'var(--space-4)', gap: 'var(--space-2)' }}>
              <button className="btn btn-secondary" onClick={() => setConfirmModal(null)}>
                Cancel
              </button>
              {/* Show PayPal button if any selected deals use PayPal */}
              {confirmModal.deals.some(d => d.paymentMethod === 'paypal') && (
                <button
                  className="btn btn-info"
                  onClick={() => processPayPalPayout(confirmModal.deals.filter(d => d.paymentMethod === 'paypal').map(d => d.id))}
                  disabled={processingPayment}
                  style={{ background: '#0070ba' }}
                >
                  {processingPayment ? 'Processing...' : `Pay via PayPal (${confirmModal.deals.filter(d => d.paymentMethod === 'paypal').length})`}
                </button>
              )}
              {/* Store credit: Prism credits the seller's Shopify account. */}
              {confirmModal.deals.some(d => isStoreCreditDeal(d)) && (
                <button
                  className="btn btn-success"
                  onClick={() => issueStoreCredit(confirmModal.deals.filter(d => isStoreCreditDeal(d)).map(d => d.id))}
                  disabled={processingPayment || confirmModal.deals.filter(d => isStoreCreditDeal(d)).some(d => creditFiguresFor(d)?.canIssue === false)}
                >
                  {processingPayment ? 'Processing...' : `Issue store credit (${confirmModal.deals.filter(d => isStoreCreditDeal(d)).length})`}
                </button>
              )}
              {/* Show Bank Transfer button if any selected deals use bank */}
              {confirmModal.deals.some(d => d.paymentMethod === 'bank') && (
                <button
                  className="btn btn-success"
                  onClick={() => markAsPaid(confirmModal.deals.filter(d => d.paymentMethod === 'bank').map(d => d.id))}
                  disabled={processingPayment}
                >
                  {processingPayment ? 'Processing...' : `Mark Bank Paid (${confirmModal.deals.filter(d => d.paymentMethod === 'bank').length})`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Copy toast */}
      {copyToast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1a1a2e',
          color: '#fff',
          padding: '10px 20px',
          borderRadius: 8,
          fontSize: 14,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          zIndex: 9999
        }}>
          {copyToast}
        </div>
      )}
    </>
  )
}

export default Payments
